import type { CalculatedInternalResponse } from 'api/__GENERATED';
import * as R from 'remeda';
import { apiUtility, domainUtility, timeUtility } from 'utility';

import { decorateDomainWithMovingValue } from './helpers';
import { toCalculatedInternal } from './toCalculatedInternal';

// Builds a normalized `Data.CalculatedInternal` domain from a list of CalculatedInternal DTOs.
// Rows with missing date or subZone components are dropped silently.
// After building the domain, `decorateDomainWithMovingValue` runs a 21-day moving sum of
// `noProcessed` per subZone and writes the result onto each item's `movingTotal` field.
// Items that fall within the moving window but have no server record are synthesised as
// placeholder entries so the selector can always find a `movingTotal` for every date.
function toCalculatedInternalDomain(args: {
  dtos: CalculatedInternalResponse[];
}) {
  const { dtos } = args;

  const calculatedInternalDomain: NormalizedDomain<Data.CalculatedInternal> = {
    allIds: [],
    byId: {},
  };

  apiUtility.iterator({
    list: dtos,
    transformer: (item) => {
      const calculatedInternal = toCalculatedInternal(item);
      if (calculatedInternal === undefined) return;
      domainUtility.mutateAdd(calculatedInternalDomain, calculatedInternal);
    },
  });

  decorateDomainWithMovingValue({
    domain: calculatedInternalDomain,
    groupKey: 'subZoneId',
    parentGroupKey: 'zoneId',
    valueKey: 'noProcessed',
    callback: ({ item, movingSum, dateId, groupId, parentGroupId }) => {
      if (R.isDefined(item)) {
        item.movingTotal = movingSum;
      } else {
        const { dayOfMonth, dayOfWeek, month, week, year } =
          timeUtility.dateFromId(dateId) as Time.Day;
        item = {
          id: R.randomString(12),
          dayOfWeek,
          month,
          year,
          week,
          dayOfMonth,
          movingTotal: movingSum,
          noProcessed: null,
          subZoneId: groupId,
          zoneId: parentGroupId,
          noBacklog: null,
          noReceived: null,
          employeeId: null,
        };
        calculatedInternalDomain.allIds.push(item.id);
      }

      calculatedInternalDomain.byId[item.id] = item;
    },
  });

  return { calculatedInternalDomain };
}

export { toCalculatedInternalDomain };
